import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Input,
  Button,
  Table,
  Radio,
  Tag,
  Space,
  Row,
  Col,
  Statistic,
  Alert,
  Tooltip,
  Typography,
  message as antdMessage,
  Spin,
} from "antd";
import {
  SendOutlined,
  ReloadOutlined,
  PlusOutlined,
  DeleteOutlined,
  NotificationOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;
const { TextArea } = Input;

const API_URL =
  "https://appmobile.svurguns.cyou/Data/api/app123/test/Notification.php";

const platformColor = (platform) => {
  const p = String(platform || "").toLowerCase();
  if (p === "android") return "green";
  if (p === "ios") return "blue";
  if (p === "web") return "gold";
  return "default";
};

const shortToken = (t) =>
  !t ? "" : t.length > 12 ? "…" + t.slice(-12) : t;

const formatDate = (str) => {
  if (!str) return "—";
  const d = new Date(String(str).replace(" ", "T"));
  if (isNaN(d.getTime())) return str;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const apiCall = async (payload) => {
  const res = await fetch(API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const text = await res.text();
  let json;
  try {
    json = JSON.parse(text);
  } catch (e) {
    throw new Error(
      "API JSON qaytarmadı: " + text.substring(0, 200)
    );
  }
  return { ok: res.ok, status: res.status, json };
};

// Backend cavabından oxunaqlı xəta mesajı çıxar.
// FCM v1 xətaları fcm.body.error.{status,message,details[].errorCode} formatında gəlir.
const extractError = (json, fallback) => {
  if (!json) return fallback;
  if (json.error && typeof json.error === "string") return json.error;

  const fcmBody = json?.fcm?.body;
  if (fcmBody?.error) {
    const fcmErr = fcmBody.error;
    const code =
      fcmErr.details?.find((d) => d.errorCode)?.errorCode || fcmErr.status;
    if (code && fcmErr.message) return `${code} — ${fcmErr.message}`;
    if (code) return String(code);
    if (fcmErr.message) return fcmErr.message;
  }

  if (json?.fcm?.http) return `FCM HTTP ${json.fcm.http}`;
  return fallback;
};

const Notifications = () => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [dataRows, setDataRows] = useState([{ key: "", value: "" }]);
  const [mode, setMode] = useState("selected"); // "selected" | "all"

  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [devicesError, setDevicesError] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);

  const loadDevices = async () => {
    setLoadingDevices(true);
    setDevicesError(null);
    try {
      const { json } = await apiCall({ action: "list_devices" });
      if (!json.success) {
        throw new Error(json.error || "API uğursuz cavab qaytardı");
      }
      const list = (json.devices || []).map((d) => ({
        ...d,
        key: d.id ?? d.token,
      }));
      setDevices(list);
    } catch (err) {
      console.error(err);
      setDevicesError(err.message);
      setDevices([]);
    } finally {
      setLoadingDevices(false);
    }
  };

  useEffect(() => {
    loadDevices();
  }, []);

  const collectData = () => {
    const out = {};
    dataRows.forEach(({ key, value }) => {
      const k = String(key || "").trim();
      if (k) out[k] = String(value ?? "").trim();
    });
    return out;
  };

  const addDataRow = () =>
    setDataRows((r) => [...r, { key: "", value: "" }]);

  const removeDataRow = (idx) =>
    setDataRows((r) => r.filter((_, i) => i !== idx));

  const updateDataRow = (idx, field, value) =>
    setDataRows((r) =>
      r.map((row, i) => (i === idx ? { ...row, [field]: value } : row))
    );

  const sendBroadcast = async (payloadBase) => {
    const startedAt = Date.now();
    try {
      const { ok, status, json } = await apiCall({
        action: "send_all",
        ...payloadBase,
      });
      const success = ok && json.success !== false;
      const elapsed = ((Date.now() - startedAt) / 1000).toFixed(2);
      const parts = [`Broadcast göndərildi (${elapsed}s)`];
      if (json.sent != null) parts.push(`sent: ${json.sent}`);
      if (json.failed != null) parts.push(`failed: ${json.failed}`);
      return [
        {
          key: "broadcast",
          ok: success,
          target: "— hamısı (broadcast) —",
          message: success
            ? parts.join(" · ")
            : extractError(json, `HTTP ${status}`),
        },
      ];
    } catch (err) {
      return [
        {
          key: "broadcast",
          ok: false,
          target: "— hamısı (broadcast) —",
          message: err.message,
        },
      ];
    }
  };

  const sendSelected = async (payloadBase) => {
    const selected = devices.filter((d) =>
      selectedRowKeys.includes(d.key)
    );
    const out = [];
    for (const dev of selected) {
      try {
        const { ok, status, json } = await apiCall({
          action: "send",
          token: dev.token,
          ...payloadBase,
        });
        const success = ok && json.success !== false;
        out.push({
          key: dev.key,
          ok: success,
          target: `user #${dev.user_id ?? "—"} · ${shortToken(dev.token)}`,
          message: success
            ? `Uğurla göndərildi${json.message_id ? ` (msg #${json.message_id})` : ""}`
            : extractError(json, `HTTP ${status}`),
        });
      } catch (err) {
        out.push({
          key: dev.key,
          ok: false,
          target: `user #${dev.user_id ?? "—"} · ${shortToken(dev.token)}`,
          message: err.message,
        });
      }
    }
    return out;
  };

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      antdMessage.warning("Zəhmət olmasa başlıq və mətn daxil edin.");
      return;
    }
    if (mode === "selected" && selectedRowKeys.length === 0) {
      antdMessage.warning(
        'Heç bir cihaz seçilməyib. Cihaz seçin və ya "Hamısına göndər" rejimini seçin.'
      );
      return;
    }

    setSending(true);
    setResults(null);

    const payloadBase = {
      title: title.trim(),
      body: body.trim(),
      data: collectData(),
    };

    try {
      const out =
        mode === "all"
          ? await sendBroadcast(payloadBase)
          : await sendSelected(payloadBase);
      setResults(out);

      const ok = out.filter((r) => r.ok).length;
      const fail = out.length - ok;
      if (fail === 0) {
        antdMessage.success(`Hamısı uğurla göndərildi (${ok})`);
      } else if (ok === 0) {
        antdMessage.error(`Hamısı uğursuz oldu (${fail})`);
      } else {
        antdMessage.warning(
          `${ok} uğurlu, ${fail} uğursuz`
        );
      }
    } catch (err) {
      antdMessage.error(err.message);
    } finally {
      setSending(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "User ID",
        dataIndex: "user_id",
        width: 100,
        render: (v) => v ?? "—",
      },
      {
        title: "Platforma",
        dataIndex: "platform",
        width: 120,
        render: (p) => (
          <Tag color={platformColor(p)}>{p || "unknown"}</Tag>
        ),
      },
      {
        title: "Token",
        dataIndex: "token",
        render: (t) => (
          <Tooltip title={t}>
            <Text code style={{ fontSize: 12 }}>
              {shortToken(t)}
            </Text>
          </Tooltip>
        ),
      },
      {
        title: "Qoşulma tarixi",
        dataIndex: "created_at",
        width: 170,
        render: (v) => formatDate(v),
      },
    ],
    []
  );

  const summary = useMemo(() => {
    if (!results) return null;
    const ok = results.filter((r) => r.ok).length;
    const fail = results.length - ok;
    return { total: results.length, ok, fail };
  }, [results]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Title level={3} style={{ margin: 0 }}>
        <NotificationOutlined /> FCM Bildiriş Paneli
      </Title>

      {/* Bildiriş məzmunu */}
      <Card title="Bildiriş Məzmunu" size="small">
        <Row gutter={16}>
          <Col xs={24} md={24}>
            <Text strong>Başlıq</Text>
            <Input
              placeholder="Məsələn: Sifariş təsdiqləndi"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              style={{ marginTop: 6, marginBottom: 12 }}
            />
          </Col>
        </Row>
        <Text strong>Mətn</Text>
        <TextArea
          rows={3}
          placeholder="Bildirişin əsas mətnini bura yazın..."
          value={body}
          onChange={(e) => setBody(e.target.value)}
          style={{ marginTop: 6, marginBottom: 16 }}
        />

        <Text strong>Əlavə Data (key-value) — istəyə bağlı</Text>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginTop: 8,
          }}
        >
          {dataRows.map((row, idx) => (
            <Space.Compact key={idx} style={{ width: "100%" }}>
              <Input
                placeholder="açar (məs. order_id)"
                value={row.key}
                onChange={(e) =>
                  updateDataRow(idx, "key", e.target.value)
                }
                style={{ width: "40%" }}
              />
              <Input
                placeholder="dəyər"
                value={row.value}
                onChange={(e) =>
                  updateDataRow(idx, "value", e.target.value)
                }
              />
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeDataRow(idx)}
                disabled={dataRows.length === 1}
              />
            </Space.Compact>
          ))}
        </div>
        <Button
          size="small"
          icon={<PlusOutlined />}
          onClick={addDataRow}
          style={{ marginTop: 8 }}
        >
          Sahə əlavə et
        </Button>
      </Card>

      {/* Göndərmə rejimi */}
      <Card title="Göndərmə Rejimi" size="small">
        <Radio.Group
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          buttonStyle="solid"
        >
          <Radio.Button value="selected">
            🎯 Seçilmiş cihaz(lar)a göndər
          </Radio.Button>
          <Radio.Button value="all">
            📡 Hamısına göndər (broadcast)
          </Radio.Button>
        </Radio.Group>
        {mode === "all" && (
          <Alert
            style={{ marginTop: 12 }}
            type="info"
            showIcon
            message="Broadcast rejimində aşağıdakı seçim nəzərə alınmır — bildiriş bütün qeydiyyatlı cihazlara göndərilir."
          />
        )}
      </Card>

      {/* Cihazlar */}
      <Card
        title={
          <span>
            Cihazlar{" "}
            <Text type="secondary" style={{ fontWeight: 400, fontSize: 13 }}>
              ({devices.length})
            </Text>
          </span>
        }
        size="small"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadDevices}
              loading={loadingDevices}
            >
              Yenilə
            </Button>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              loading={sending}
            >
              Göndər
            </Button>
          </Space>
        }
      >
        {devicesError && (
          <Alert
            type="error"
            showIcon
            message="Cihazlar yüklənmədi"
            description={devicesError}
            style={{ marginBottom: 12 }}
          />
        )}
        <Spin spinning={loadingDevices}>
          <Table
            size="small"
            rowSelection={
              mode === "selected"
                ? {
                    selectedRowKeys,
                    onChange: setSelectedRowKeys,
                  }
                : undefined
            }
            columns={columns}
            dataSource={devices}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            scroll={{ x: true }}
            locale={{ emptyText: "Heç bir cihaz tapılmadı" }}
          />
        </Spin>
      </Card>

      {/* Nəticələr */}
      {results && (
        <Card title="Nəticələr" size="small">
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={8}>
              <Card size="small" style={{ background: "#eef2ff" }}>
                <Statistic
                  title="Cəmi"
                  value={summary.total}
                  valueStyle={{ color: "#4f46e5" }}
                />
              </Card>
            </Col>
            <Col xs={8}>
              <Card size="small" style={{ background: "#ecfdf5" }}>
                <Statistic
                  title="Uğurlu"
                  value={summary.ok}
                  valueStyle={{ color: "#059669" }}
                />
              </Card>
            </Col>
            <Col xs={8}>
              <Card size="small" style={{ background: "#fef2f2" }}>
                <Statistic
                  title="Uğursuz"
                  value={summary.fail}
                  valueStyle={{ color: "#dc2626" }}
                />
              </Card>
            </Col>
          </Row>

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {results.map((r) => (
              <Alert
                key={r.key}
                type={r.ok ? "success" : "error"}
                showIcon
                message={
                  <Space style={{ width: "100%", justifyContent: "space-between" }}>
                    <Text style={{ fontFamily: "monospace", fontSize: 12 }}>
                      {r.target}
                    </Text>
                    <Text>{r.message}</Text>
                  </Space>
                }
              />
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default Notifications;
